import { useEffect, useState } from 'react';

interface Particle {
  id: number;
  x: number;
  y: number;
  size: number;
  speed: number;
  direction: number;
  opacity: number;
  shape: 'circle' | 'ring' | 'star' | 'diamond' | 'triangle' | 'cloud' | 'palette' | 'brush' | 'pencil' | 'crayon' | 'heart' | 'butterfly' | 'flower' | 'sun' | 'moon' | 'rainbow' | 'sparkle' | 'balloon';
}

const FloatingParticles = () => {
  const [particles, setParticles] = useState<Particle[]>([]);

  const getShapeStyles = (shape: Particle['shape'], size: number) => {
    const baseClasses = "absolute animate-pulse";
    const baseStyles = {
      animationDuration: `${3 + Math.random() * 2}s`,
    };

    switch (shape) {
      case 'circle':
        return {
          className: `${baseClasses} rounded-full bg-primary/20`,
          style: baseStyles,
        };
      case 'ring':
        return {
          className: `${baseClasses} rounded-full border-2 border-primary/30`,
          style: { ...baseStyles, backgroundColor: 'transparent' },
        };
      case 'star':
        return {
          className: `${baseClasses} text-primary/30`,
          style: { ...baseStyles, fontSize: `${size}px`, lineHeight: 1 },
          content: '✦',
        };
      case 'diamond':
        return {
          className: `${baseClasses} bg-primary/20`,
          style: { ...baseStyles, transform: 'rotate(45deg)' },
        };
      case 'triangle':
        return {
          className: `${baseClasses}`,
          style: {
            ...baseStyles,
            width: 0,
            height: 0,
            borderLeft: `${size/2}px solid transparent`,
            borderRight: `${size/2}px solid transparent`,
            borderBottom: `${size}px solid hsl(var(--primary) / 0.2)`,
          },
        };
      case 'cloud':
        return {
          className: `${baseClasses} text-primary/25`,
          style: { ...baseStyles, fontSize: `${size}px`, lineHeight: 1 },
          content: '☁',
        };
      case 'palette':
        return {
          className: `${baseClasses} text-primary/25`,
          style: { ...baseStyles, fontSize: `${size}px`, lineHeight: 1 },
          content: '🎨',
        };
      case 'brush':
        return {
          className: `${baseClasses} text-primary/25`,
          style: { ...baseStyles, fontSize: `${size}px`, lineHeight: 1 },
          content: '🖌️',
        };
      case 'pencil':
        return {
          className: `${baseClasses} text-primary/25`,
          style: { ...baseStyles, fontSize: `${size}px`, lineHeight: 1 },
          content: '✏️',
        };
      case 'crayon':
        return {
          className: `${baseClasses} text-primary/25`,
          style: { ...baseStyles, fontSize: `${size}px`, lineHeight: 1 },
          content: '🖍️',
        };
      case 'heart':
        return {
          className: `${baseClasses} text-primary/25`,
          style: { ...baseStyles, fontSize: `${size}px`, lineHeight: 1 },
          content: '❤️',
        };
      case 'butterfly':
        return {
          className: `${baseClasses} text-primary/25`,
          style: { ...baseStyles, fontSize: `${size}px`, lineHeight: 1 },
          content: '🦋',
        };
      case 'flower':
        return {
          className: `${baseClasses} text-primary/25`,
          style: { ...baseStyles, fontSize: `${size}px`, lineHeight: 1 },
          content: '🌸',
        };
      case 'sun':
        return {
          className: `${baseClasses} text-primary/25`,
          style: { ...baseStyles, fontSize: `${size}px`, lineHeight: 1 },
          content: '☀️',
        };
      case 'moon':
        return {
          className: `${baseClasses} text-primary/25`,
          style: { ...baseStyles, fontSize: `${size}px`, lineHeight: 1 },
          content: '🌙',
        };
      case 'rainbow':
        return {
          className: `${baseClasses} text-primary/25`,
          style: { ...baseStyles, fontSize: `${size}px`, lineHeight: 1 },
          content: '🌈',
        };
      case 'sparkle':
        return {
          className: `${baseClasses} text-primary/25`,
          style: { ...baseStyles, fontSize: `${size}px`, lineHeight: 1 },
          content: '✨',
        };
      case 'balloon':
        return {
          className: `${baseClasses} text-primary/25`,
          style: { ...baseStyles, fontSize: `${size}px`, lineHeight: 1 },
          content: '🎈',
        };
      default:
        return {
          className: `${baseClasses} rounded-full bg-primary/20`,
          style: baseStyles,
        };
    }
  };

  useEffect(() => {
    const shapes = [
      'circle', 'ring', 'star', 'diamond', 'triangle', 'cloud',
      'palette', 'brush', 'pencil', 'crayon', 'heart', 'butterfly',
      'flower', 'sun', 'moon', 'rainbow', 'sparkle', 'balloon'
    ] as const; // Add 'as const' to preserve literal types
    
    const createParticles = () => {
      const newParticles: Particle[] = [];
      for (let i = 0; i < 50; i++) {
        newParticles.push({
          id: i,
          x: Math.random() * 100,
          y: Math.random() * 100,
          size: Math.random() * 8 + 4,
          speed: Math.random() * 2 + 1,
          direction: Math.random() * 360,
          opacity: Math.random() * 0.6 + 0.2,
          shape: shapes[Math.floor(Math.random() * shapes.length)],
        });
      }
      setParticles(newParticles);
    };

    createParticles();

    const interval = setInterval(() => {
      setParticles(prev => 
        prev.map(particle => ({
          ...particle,
          x: (particle.x + Math.cos(particle.direction) * particle.speed * 0.1) % 100,
          y: (particle.y + Math.sin(particle.direction) * particle.speed * 0.1) % 100,
        }))
      );
    }, 50);

    return () => clearInterval(interval);
  }, []);

  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none" style={{ contain: 'layout style paint' }}>
      {particles.map(particle => {
        const shapeConfig = getShapeStyles(particle.shape, particle.size);
        
        return (
          <div
            key={particle.id}
            className={shapeConfig.className}
            style={{
              left: `${particle.x}%`,
              top: `${particle.y}%`,
              width: particle.shape === 'triangle' ? 'auto' : `${particle.size}px`,
              height: particle.shape === 'triangle' ? 'auto' : `${particle.size}px`,
              opacity: particle.opacity,
              ...shapeConfig.style,
            }}
          >
            {shapeConfig.content}
          </div>
        );
      })}
    </div>
  );
};

export default FloatingParticles;